import { useDispatch, useSelector } from "@/core/redux/store";
import { getProductCategoriesThunk } from "@/core/redux/thunks/specificationThunk";
import { Button } from "@/shared/components/ui";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/shared/components/ui/carousel";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Progress } from "@/shared/components/ui/progress";
import { Separator } from "@/shared/components/ui/separator";
import { specificationService } from "@/shared/services/api/specification-service";
import { ChevronDown, Compass, Info, Loader2, X } from "lucide-react";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { setThresHoldData } from "@/core/redux/slices/specification";
import { RealtimeChannel } from "@supabase/supabase-js";
import { _supabaseClient } from "@/shared/services/supabase/supabase-instance";
import { loggerService } from "@/shared/services/logger-service";
import {
  trackSpecificationCategorySelected,
  trackSpecificationAIGenerationInitiated,
  trackSpecificationAIGenerationComplete,
} from "@/lib/rum-actions";
import { capitalizeWords } from "@/shared/helpers/text-helper";

const ProductCategory = () => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { thresHoldData, productCategories } = useSelector(
    (state) => state.specification
  );

  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslation();
  const { specification } = useSelector(
    (state) => state.specification.specification
  );
  const specType = specification?.specType;
  const dispatch = useDispatch();
  const [loadingTip, setLoadingTip] = useState(false);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [loadingCategorySelection, setLoadingCategorySelection] =
    useState(false);

  const me = useSelector((state) => state.user.me);
  const categoryNames = specification?.categoryNames;
  const categoryList = categoryNames?.categoryList ?? [];
  const sortedCategoryList = [...categoryList]?.sort(
    (a, b) => b.score - a.score
  );
  const selectedBaseCategory = categoryNames?.selectedCategory;

  // Supabase channel reference
  const categoryChannelRef = useRef<RealtimeChannel | null>(null);

  // Mevcut kategorileri filtrele
  const filteredCategoryList = sortedCategoryList.filter((category) =>
    category.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Product categories'den gelen kategorileri filtrele
  const productCategoryList =
    productCategories.data?.categories?.filter((category) =>
      category.categoryName.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  // Tüm kategorileri birleştir (mevcut + product categories)
  const existingCategories = filteredCategoryList.map((cat) => ({
    category: cat.category,
    score: cat.score,
    source: "existing" as const,
  }));

  const newCategories = productCategoryList
    .filter(
      (cat) =>
        // Mevcut kategorilerde olmayan kategorileri al
        !filteredCategoryList.some(
          (existing) =>
            existing.category.toLowerCase() === cat.categoryName.toLowerCase()
        )
    )
    .map((cat) => ({
      category: cat.categoryName,
      score: 0, // Product categories için score yok
      source: "product" as const,
    }));

  // Tüm kategorileri birleştir ve sırala
  const allCategories = [...existingCategories, ...newCategories].sort(
    (a, b) => {
      // Önce score'a göre (yüksek score önce), sonra alfabetik
      if (a.score !== b.score) {
        return b.score - a.score;
      }
      return a.category.localeCompare(b.category);
    }
  );

  // When specType is 'service', restrict visible categories to top-3 from sortedCategoryList
  const visibleCategories =
    specType === "service"
      ? sortedCategoryList.slice(0, 3).map((c) => ({
        category: c.category,
        score: c.score,
        source: "existing" as const,
      }))
      : allCategories;

  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    selectedBaseCategory ?? sortedCategoryList?.[0]?.category
  );
  const [selectedCategoryScore, setSelectedCategoryScore] = useState<
    number | null
  >(
    sortedCategoryList?.find(
      (category) => category.category === selectedCategory
    )?.score ?? null
  );
  const unspecifiedCategory = !categoryNames || categoryList.length == 0;

  const badgeColor = (score: number) => {
    if (unspecifiedCategory)
      return {
        border: "1px solid #E4E4E7",
        background: "transparent",
      };
    if (score >= 0 && score <= 59)
      return {
        border: "1px solid rgba(192, 42, 20, 0.20)",
        background: "rgba(192, 42, 20, 0.10)",
      };
    else if (score >= 60 && score <= 74)
      return {
        border: "1px solid rgba(247, 168, 107, 0.20)",
        background: "rgba(247, 168, 107, 0.10)",
      };
    else if (score >= 75 && score <= 100)
      return {
        border: "1px solid rgba(80, 172, 90, 0.20)",
        background: "rgba(80, 172, 90, 0.10)",
      };
  };

  const progressColor = (score: number) => {
    if (unspecifiedCategory) return "bg-muted-foreground";
    else if (score >= 0 && score <= 59) return "#C02A14";
    else if (score >= 60 && score <= 74) return "#EF8432";
    else if (score >= 75 && score <= 100) return "#50AC5A";
  };

  const badgeTextColor = (score: number) => {
    if (unspecifiedCategory) return "text-muted-foreground";
    else if (score >= 0 && score <= 59) return "text-[#C02A14]";
    else if (score >= 60 && score <= 74) return "text-[#EF8432]";
    else if (score >= 75 && score <= 100) return "text-[#50AC5A]";
  };

  const percentageColor = (score: number) => {
    if (unspecifiedCategory) return "bg-muted-foreground";
    else if (score >= 0 && score <= 59) return "bg-[#C02A14]";
    else if (score >= 60 && score <= 74) return "bg-[#EF8432]";
    else if (score >= 75 && score <= 100) return "bg-[#50AC5A]";
  };

  const badgeText = (score: number) => {
    if (unspecifiedCategory) return "0";
    else if (score >= 0 && score <= 59) return t("low");
    else if (score >= 60 && score <= 74) return t("medium");
    else if (score >= 75 && score <= 100) return t("high");
  };

  const productCategoryDescription = (score: number) => {
    if (score >= 0 && score <= 59) return t("low-product-category-description");
    else if (score >= 60 && score <= 74)
      return t("medium-product-category-description");
    else if (score >= 75 && score <= 100)
      return t("high-product-category-description");
  };

  // --------- HANDLERS (service / product / manual) ---------

  const handleServiceCategorySelect = async (category: string) => {
    if (!me?.id || !specification?.id || !category) return;

    // sadece top-3 için izin ver
    const top3 = sortedCategoryList.slice(0, 3).map((c) => c.category);
    if (!top3.includes(category)) return;

    setSelectedCategory(category);
    const selectedScore =
      sortedCategoryList.find((c) => c.category === category)?.score ?? null;
    setSelectedCategoryScore(selectedScore);

    setLoadingCategorySelection(true);
    try {
      const response = (await specificationService.fetchCategorySpecificScore({
        specificationId: specification.id,
        userId: me.id,
        region: "turkey",
        selectedCategoryName: category,
        fromDb: true, // service'te sadece mevcut top-3 üzerinden gidiyoruz
      })) as any;

      if (response) {
        dispatch(
          setThresHoldData({
            ...response.data,
            clarification_questions: response.improvement_tips,
            understanding_score: response.score,
          })
        );
        if (typeof response.score === "number") {
          setSelectedCategoryScore(response.score);
        }
      }
    } catch (error) {
      console.error("Service category detail error:", error);
    } finally {
      setLoadingCategorySelection(false);
    }
  };

  const handleProductCategorySelect = async (category: string) => {
    if (!me?.id || !specification?.id || !category) return;

    setSelectedCategory(category);
    const selectedScore =
      sortedCategoryList.find((c) => c.category === category)?.score ?? 0;
    setSelectedCategoryScore(selectedScore);

    setLoadingCategorySelection(true);
    try {
      const response = (await specificationService.fetchCategorySpecificScore({
        specificationId: specification.id,
        userId: me.id,
        region: "turkey",
        selectedCategoryName: category,
        fromDb: true,
      })) as any;

      if (response) {
        dispatch(
          setThresHoldData({
            ...response.data,
            clarification_questions: response.improvement_tips,
            understanding_score: response.score,
          })
        );
        if (typeof response.score === "number") {
          setSelectedCategoryScore(response.score);
        }
      }
    } catch (error) {
      console.error("Product category score error:", error);
    } finally {
      setLoadingCategorySelection(false);
    }
  };

  const handleManualCategorySelect = async (category: string) => {
    if (!me?.id || !specification?.id || !category) return;

    setSelectedCategory(category);
    const selectedScore =
      sortedCategoryList.find((c) => c.category === category)?.score ?? 0;
    setSelectedCategoryScore(selectedScore);

    setLoadingCategorySelection(true);
    try {
      const response = (await specificationService.fetchCategorySpecificScore({
        specificationId: specification.id,
        userId: me.id,
        region: "turkey",
        selectedCategoryName: category,
        fromDb: true,
      })) as any;

      if (response) {
        dispatch(
          setThresHoldData({
            ...response.data,
            clarification_questions: response.improvement_tips,
            understanding_score: response.score,
          })
        );
        if (typeof response.score === "number") {
          setSelectedCategoryScore(response.score);
        }
      }
    } catch (error) {
      console.error("Manual category score error:", error);
    } finally {
      setLoadingCategorySelection(false);
    }
  };

  const handleSelectCategory = async (category: string) => {
    if (unspecifiedCategory) return;
    if (!category) return;
    if (!me?.id || !specification?.id) return;
    if (category === selectedCategory) return;
    setIsDropdownOpen(false);

    // RUM-004: Track category selected
    trackSpecificationCategorySelected(
      specification.id,
      category, // Using category name as categoryId since there's no separate id
      category,
      'manual'
    );

    if (specType === "service") {
      return handleServiceCategorySelect(category);
    }

    if (specType === "product") {
      return handleProductCategorySelect(category);
    }

    // default: manual + diğer tipler
    return handleManualCategorySelect(category);
  };

  // --------- EFFECTS & LISTENERS ---------

  useEffect(() => {
    if (!categoryNames || !categoryNames.selectedCategory) {
      return;
    }

    // Sadece selectedCategory gerçekten değiştiyse güncelle
    if (categoryNames.selectedCategory !== selectedCategory) {
      setSelectedCategory(categoryNames.selectedCategory);
      setSelectedCategoryScore(
        sortedCategoryList?.find(
          (category) => category.category === categoryNames.selectedCategory
        )?.score ?? null
      );
    }
  }, [categoryNames?.selectedCategory, selectedCategory, sortedCategoryList]);

  // İlk yüklemede specification.tips → thresHoldData sync
  useEffect(() => {
    if (
      !thresHoldData &&
      specification?.tips?.thresholdTips &&
      specification.tips.thresholdTips.length > 0
    ) {
      dispatch(
        setThresHoldData({
          clarification_questions: specification.tips.thresholdTips,
          understanding_score: selectedCategoryScore ?? 0,
        })
      );
    }
  }, [specification?.tips, thresHoldData, dispatch, selectedCategoryScore]);

  // Combobox dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        comboboxOpen &&
        !(event.target as Element).closest(".combobox-container")
      ) {
        setComboboxOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [comboboxOpen]);

  // Debounced search fonksiyonu - minimum 3 harf ile arama
  const debouncedSearch = useCallback(
    debounce(async (searchValue: string) => {
      if (searchValue.length >= 3 && me?.id && specification?.id) {
        try {
          await dispatch(
            getProductCategoriesThunk({
              search: searchValue,
            })
          );
        } catch (error) {
          console.error("Product categories search error:", error);
        }
      }
    }, 500),
    [dispatch, me?.id, specification?.id]
  );

  // Search term değiştiğinde debounced search'ü çağır
  useEffect(() => {
    if (specType === "service") return; // service için search disable
    if (searchTerm.length >= 3) {
      debouncedSearch(searchTerm);
    } else if (searchTerm.length === 0) {
      // Search temizlendiğinde product categories'i de temizleyebiliriz (şu an sadece UI)
    }
  }, [searchTerm, debouncedSearch, specType]);

  // Supabase channel listener for category names updates
  const listenCategoryUpdates = useCallback(() => {
    if (!specification?.id) return;

    // Önceki kanalı kapat
    if (categoryChannelRef.current) {
      categoryChannelRef.current.unsubscribe();
      categoryChannelRef.current = null;
    }

    loggerService.log(
      "Category updates listening started for specification:",
      specification.id
    );

    // Yeni kanalı oluştur
    categoryChannelRef.current = _supabaseClient
      .channel(`specification-notify:${specification.id}`)
      .on(
        "broadcast",
        { event: "client-requirements-threshold-completed" },
        (payload) => {
          loggerService.log("Category names update received:", payload);

          const data = payload.payload as {
            category_names: Array<{
              category: string;
              score: number;
            }>;
            selectedCategory: string;
          };

          if (data && data.category_names) {
            // Update selected category and score
            setSelectedCategory(data.selectedCategory);
            const selectedCategoryData = data.category_names.find(
              (cat) => cat.category === data.selectedCategory
            );
            if (selectedCategoryData) {
              setSelectedCategoryScore(selectedCategoryData.score);
            }

            loggerService.log("Category updated to:", data.selectedCategory);
          }
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          loggerService.log("Category updates channel subscribed");
        } else {
          loggerService.log("Category updates channel status:", status);
        }
      });

    return () => {
      if (categoryChannelRef.current) {
        categoryChannelRef.current.unsubscribe();
        categoryChannelRef.current = null;
        loggerService.log("Category updates channel unsubscribed");
      }
    };
  }, [specification?.id]);

  // Initialize channel listener
  useEffect(() => {
    const cleanup = listenCategoryUpdates();

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [listenCategoryUpdates]);

  const generateTipItemHandler = async (tip: string) => {
    if (!me?.id || !specification?.id || !tip) return;
    setLoadingTip(true);

    // RUM-002: Track AI generation initiated
    const startTime = Date.now();
    trackSpecificationAIGenerationInitiated(
      specification.id,
      selectedCategory || 'unknown',
      tip.length
    );

    try {
      const response = await specificationService.generateTipItems({
        tip: tip,
        specificationId: specification?.id,
        userId: me?.id,
        region: "turkey",
      });

      // RUM-003: Track AI generation complete
      const generationTimeMs = Date.now() - startTime;
      trackSpecificationAIGenerationComplete(
        specification.id,
        specification.itemCount || 0,
        generationTimeMs,
        response?.success || true
      );
    } finally {
      setTimeout(() => {
        setLoadingTip(false);
        setIsDropdownOpen(false);
      }, 1000);
    }
  };

  return (
    <div className="flex flex-col px-1.5 py-2 ">
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger
          asChild
          disabled={unspecifiedCategory}
          className="cursor-pointer"
        >
          <div className="w-full px-5 flex items-center justify-between cursor-pointer">
            <Separator
              orientation="vertical"
              className="bg-zinc-200 mr-4"
              style={{
                height: "16px",
              }}
            />

            <div
              className={`relative flex items-center gap-1 justify-between w-full px-[12px] py-[6px] rounded-[8px] bg-white cursor-pointer hover:bg-zinc-100 ${isDropdownOpen ? "bg-zinc-100" : ""
                }`}
            >
              {loadingCategorySelection || productCategories.loading ? (
                <DotLottieReact
                  src="/animations/lottie/loading-spinner.json"
                  style={{
                    width: "35px",
                    height: "35px",
                    marginLeft: "-12px",
                  }}
                  autoplay={true}
                  loop={true}
                />
              ) : (
                <Compass
                  size={16}
                  className="mr-1 text-zinc-900 -ml-1 cursor-pointer"
                />
              )}

              <span
                className={`w-[60%] font-600 truncate text-[13px] cursor-pointer ${unspecifiedCategory ? "text-muted-foreground" : ""
                  }`}
              >
                {unspecifiedCategory
                  ? t("product-category")
                  : capitalizeWords(selectedCategory?.substring(0, 20) || "")}
              </span>
              {unspecifiedCategory && (
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                  <PopoverTrigger
                    asChild
                    onMouseEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsPopoverOpen(true);
                    }}
                    onMouseLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsPopoverOpen(false);
                    }}
                  >
                    <Info size={10} />
                  </PopoverTrigger>
                  <PopoverContent
                    className="relative flex flex-col gap-1  px-5 pt-4 pb-2.5 w-[264px] h-auto shadow-sm border-none"
                    style={{
                      zIndex: 99999999,
                    }}
                    side={"top"}
                    sideOffset={15}
                    align="center"
                    alignOffset={10}
                  >
                    <span className="text-muted-foreground font-600 text-[14px]">
                      {t("locked-category")}
                    </span>
                    <span className="text-muted-foreground font-500 text-[13px]">
                      {t("locked-category-description")}
                    </span>
                    <div className="absolute -top-[9px] rounded-[4px] right-[50%] translate-x-[50%] w-[15px] h-[15px] bg-white rotate-45 border-l-2 border-t-2 border-[#e5e7eb]" />
                  </PopoverContent>
                </Popover>
              )}
              <div className="flex items-center gap-1.5">
                <div
                  className="px-[10px] py-[2px] rounded-[100px] w-[43px]"
                  style={badgeColor(selectedCategoryScore ?? 0)}
                >
                  <span
                    className={`text-xs font-600   ${badgeTextColor(
                      selectedCategoryScore || 0
                    )}`}
                  >
                    {unspecifiedCategory ? "0" : selectedCategoryScore}%
                  </span>
                </div>

                <ChevronDown size={15} />
              </div>

              <div
                className={`absolute h-[2px] rounded-b-[7px] ${percentageColor(
                  selectedCategoryScore ?? 0
                )} left-0 bottom-0`}
                style={{
                  zIndex: 99999999,
                  width: `${selectedCategoryScore ?? 0}%`,
                }}
              />

              {!unspecifiedCategory && (
                <div
                  className={`absolute h-[2px] left-0 bottom-0 rounded-b-[6px]`}
                  style={{
                    width: `100%`,
                    background: badgeColor(selectedCategoryScore ?? 0)
                      ?.background,
                  }}
                />
              )}
            </div>
            <Separator
              orientation="vertical"
              className="bg-zinc-200 ml-4"
              style={{
                height: "16px",
              }}
            />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[372px] !overflow-visible p-0 rounded-[12px] mt-1 pb-3 ">
          <div className="flex flex-row items-center justify-between gap-2 mb-2 px-3 pt-1.5">
            <span className="text-[14px] font-600 truncate w-[70%]">
              {t("product-category")}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="w-fit border-none"
              onClick={() => setIsDropdownOpen(false)}
            >
              <X size={15} />
            </Button>
          </div>
          <Separator />
          <div className="flex flex-col px-3">
            <span className="text-[12px] font-600 text-muted-foreground pt-3 pb-1 px-0.5">
              {t("selected-category")}
            </span>
            <div className="relative combobox-container">
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={comboboxOpen}
                className={`w-full justify-between h-[36px] px-3 text-[14px] font-600 truncate text-left ${specification.specificationType === "ai_generated"
                  ? "!opacity-100"
                  : ""
                  }`}
                onClick={() => setComboboxOpen(!comboboxOpen)}
              >
                <span className="truncate">
                  {selectedCategory ? capitalizeWords(selectedCategory) : t("select-category")}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {comboboxOpen && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                  <Command className="w-full max-h-[250px]">
                    <CommandInput
                      placeholder={t("search-category")}
                      className="h-[36px] px-2 focus-visible:ring-0"
                      value={searchTerm}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        // if (e.key === "Enter") {
                        //   // service: search/enter disabled
                        //   if (specType === "service") return;

                        //   // product: sadece DB kategorisine tam eşleşme varsa
                        //   if (specType === "product") {
                        //     const match = productCategoryList.find(
                        //       (c) =>
                        //         c.categoryName.toLowerCase() ===
                        //         searchTerm.toLowerCase()
                        //     );
                        //     if (match) {
                        //       handleSelectCategory(match.categoryName, true);
                        //       setComboboxOpen(false);
                        //     }
                        //     return;
                        //   }

                        //   // manual + diğer tipler: serbest
                        //   handleSelectCategory(searchTerm, true);
                        //   setComboboxOpen(false);
                        // }
                      }}
                      onValueChange={setSearchTerm}
                    />
                    <CommandList className="max-h-[200px] overflow-y-auto overflow-x-hidden">
                      {productCategories.loading && searchTerm.length >= 3 && (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="ml-2 text-sm text-muted-foreground">
                            {t("searching-categories")}
                          </span>
                        </div>
                      )}
                      {loadingCategorySelection && (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="ml-2 text-sm text-muted-foreground">
                            {t("processing-category")}
                          </span>
                        </div>
                      )}
                      <CommandEmpty>
                        {searchTerm.length < 3
                          ? t("type-at-least-3-characters")
                          : t("no-category-found")}
                      </CommandEmpty>
                      <CommandGroup>
                        {visibleCategories.map((category, index) => (
                          <CommandItem
                            key={`${category.source}-${category.category}-${index}`}
                            value={category.category}
                            onSelect={async () => {
                              if (!loadingCategorySelection) {
                                try {
                                  await handleSelectCategory(category.category);
                                } finally {
                                  setComboboxOpen(false);
                                }
                              }
                            }}
                            className={`cursor-pointer ${selectedCategory === category.category
                              ? "bg-accent text-accent-foreground"
                              : ""
                              }`}
                          >
                            <div className="flex items-center justify-between w-full">
                              <span className="text-[14px] font-600">
                                {capitalizeWords(category.category)}
                              </span>
                              {category.score > 0 && (
                                <div
                                  className="px-[10px] py-[2px] rounded-[100px] w-[43px]"
                                  style={badgeColor(category.score ?? 0)}
                                >
                                  <span
                                    className={`text-xs font-600 ${badgeTextColor(
                                      category.score ?? 0
                                    )}`}
                                  >
                                    {category.score}%
                                  </span>
                                </div>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col mb-2 pt-4 px-3 justify-center">
            <div className="flex flex-row gap-1 w-full items-center px-1">
              <span
                className="text-[24px] text-zinc-950 count-inter "
                style={{
                  fontWeight: 600,
                }}
              >
                {selectedCategoryScore}%
              </span>
              <div
                className="px-[10px] py-[2px] flex items-center justify-center rounded-[100px] min-w-[54px] w-fit h-[24px]"
                style={badgeColor(selectedCategoryScore ?? 0)}
              >
                <div
                  className={`text-xs font-600 ${badgeTextColor(
                    selectedCategoryScore ?? 0
                  )}`}
                >
                  {badgeText(selectedCategoryScore ?? 0)}
                </div>
              </div>
            </div>
            <div className="w-full px-1 pt-1.5 pb-2">
              <Progress
                value={selectedCategoryScore ?? 0}
                className="w-full h-[5px] px-2"
                progressColor={progressColor(selectedCategoryScore ?? 0)}
              />
            </div>
            <div
              className="text-[12px] font-500 text-muted-foreground px-1.5"
              dangerouslySetInnerHTML={{
                __html:
                  productCategoryDescription(selectedCategoryScore ?? 0) || "",
              }}
            />

            <Separator className="my-2" />
          </div>
          <div className="flex flex-col px-3">
            <div
              className="text-[12px] font-500 text-muted-foreground px-1.5 "
              style={{
                lineHeight: "20px",
              }}
              dangerouslySetInnerHTML={{
                __html: t("product-category-description"),
              }}
            />
          </div>

          {thresHoldData &&
            thresHoldData?.clarification_questions?.length > 0 && (
              <div className="px-3 mt-2.5 ">
                <Carousel className="w-full border-1 border-zinc-200">
                  <div className="flex items-center gap-1 absolute top-3 right-3 z-10">
                    <CarouselPrevious />
                    <CarouselNext />
                  </div>

                  <CarouselContent className="w-full ml-0 border-1 border-zinc-200">
                    {thresHoldData?.clarification_questions?.map(
                      (item, index) => (
                        <CarouselItem
                          key={item}
                          className=" max-h-[300px] flex flex-col justify-center pt-2.5 pb-2  rounded-[12px] mb-[11px]"
                          style={{
                            backgroundColor: "rgba(228, 228, 231, 0.20)",
                          }}
                        >
                          <div className="w-[250px] flex items-center justify-center flex-row ">
                            <img
                              src="/images/lamba.png"
                              alt=""
                              className="w-[24px] h-[25px] mr-2 -ml-1 -mt-1"
                            />
                            <div className="w-[250px] flex flex-col gap-1">
                              <span className="text-[13px] text-zinc-950 font-500">
                                {t("tip-found", {
                                  count:
                                    thresHoldData?.clarification_questions
                                      ?.length,
                                })}
                              </span>
                              <span className="text-[11px] text-zinc-500 font-500 -mt-1">
                                {t("tips-of-total", {
                                  count: index + 1,
                                  total:
                                    thresHoldData?.clarification_questions
                                      ?.length,
                                })}
                              </span>
                            </div>
                          </div>
                          <div className="mt-3">
                            <span
                              className="text-[13px] text-muted-foreground font-500"
                              style={{
                                lineHeight: "20px",
                              }}
                            >
                              {item}
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full h-[36px] -ml-2 mt-4"
                            onClick={() => generateTipItemHandler(item)}
                          >
                            {loadingTip && (
                              <Loader2 size={15} className="animate-spin" />
                            )}
                            {t("apply")}
                          </Button>
                        </CarouselItem>
                      )
                    )}
                  </CarouselContent>
                </Carousel>
              </div>
            )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ProductCategory;
